// @ts-nocheck
import { useEffect, useRef } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import {
  StyledContributionsByChannelsCard,
  StyledSubtitlePrimary,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/common.styles';
import { useCoreContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreContext';
import { CONTRIBUTION_BY_CHANNEL } from 'modules/intelligentInsights/brandAffinityInsights/constants';
import {
  defaultCampaignImpactCreators,
  useDataManagementContext,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import { getOrElse } from 'fp-ts/lib/Option';
import {
  CampaignImpactChartData,
  DirectionEnum,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import { formatCount } from 'modules/intelligentInsights/domain.utils';
import { caption, colors, fonts, fontWeights, spacing } from 'ui/theme';

export const directionDataConfig = {
  [DirectionEnum.up]: {
    color: colors.cyan80,
    name: 'Improved movement',
  },
  [DirectionEnum.down]: {
    color: colors.midnight80,
    name: 'Degraded movement',
  },
  [DirectionEnum.unchanged]: {
    color: colors.grey50,
    name: 'No impact',
  },
};

export const createBarChartOptions = (campaignImpactData: CampaignImpactChartData) => {
  return {
    reflow: false,
    chart: {
      type: 'bar',
      width: null,
      height: 336,
      backgroundColor: colors.backgroundTertiary,
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: campaignImpactData.mediums.filter(Boolean),
      labels: {
        style: {
          ...caption,
          fontFamily: fonts.sansSerif,
          color: colors.textSecondary,
        },
      },
      lineColor: colors.grey15,
    },
    yAxis: {
      minRange: 1,
      lineColor: colors.grey15,
      gridLineDashStyle: 'Dash',
      tickWidth: 1,
      tickColor: colors.grey15,
      reversedStacks: false,
      lineWidth: 1,
      labels: {
        format: '{value}',
        style: {
          ...caption,
          fontFamily: fonts.sansSerif,
          color: colors.textSecondary,
        },
      },
      min: 0,
      title: {
        text: 'Number of campaigns sent',
        margin: 12,
        style: {
          ...caption,
          fontFamily: fonts.sansSerif,
          color: colors.grey50,
        },
      },
    },
    plotOptions: {
      bar: {
        stacking: 'normal',
        minPointLength: 3,
        pointWidth: 28,
      },
      series: {
        borderRadius: 4,
      },
    },
    legend: {
      itemDistance: 15,
      itemStyle: {
        ...caption,
        color: colors.textSecondary,
        fontWeight: fontWeights.medium,
        fontFamily: fonts.sansSerif,
      },
    },
    series: campaignImpactData.chartData.map((chartDataItem) => ({
      ...directionDataConfig[chartDataItem.name],
      data: chartDataItem.data,
    })),
    tooltip: {
      borderColor: colors.grey80,
      borderRadius: 4,
      backgroundColor: colors.grey80,
      style: {
        color: colors.white,
        fontFamily: fonts.sansSerif,
      },
      useHTML: true,
      pointFormatter: function pointFormatter() {
        const point = this;
        return `<div style="display: flex; align-items: center;gap: ${
          spacing.half
        };"><span style="color:${point.color};font-size:12px;">\u25CF</span>
        <span style="color:${colors.white};">${point.series.name}: ${formatCount(point.y)}</span>
        </div>`;
      },
    },
  };
};

export const ContributionsByChannel = () => {
  const chartComponentRef = useRef(null);
  const { selectedAffinityTab } = useCoreContext();
  const { managedInsightsData } = useDataManagementContext();
  useEffect(() => {
    const handleResize = () => {
      if (chartComponentRef.current) {
        const { chart } = chartComponentRef.current;
        if (chart.container && window.innerWidth < 1280) {
          chart.setSize(window.innerWidth - 100, null);
        } else {
          chart.setSize('528', null);
        }
      }
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const dataCreators = getOrElse(() => defaultCampaignImpactCreators)(
    managedInsightsData.campaignImpact,
  );
  const campaignImpactData = dataCreators.getCampaignChartData(selectedAffinityTab);

  if (campaignImpactData.mediums.filter(Boolean).length <= 0) {
    return null;
  }
  const stackedBarChartOptions = createBarChartOptions(campaignImpactData);

  return (
    <StyledContributionsByChannelsCard>
      <StyledSubtitlePrimary>{CONTRIBUTION_BY_CHANNEL}</StyledSubtitlePrimary>
      <HighchartsReact
        constructorType="chart"
        highcharts={Highcharts}
        options={stackedBarChartOptions as any}
        ref={chartComponentRef}
      />
    </StyledContributionsByChannelsCard>
  );
};
